import { QueryClientProvider } from '@tanstack/react-query';
import { styled } from 'styled-components';
import { LoadingSpinner, tk } from '@sunwebgroup/ui-components';
import { useResults } from '@/scopes/search/search/domain/hooks';
import { createQueryStringToSearchStateMapper } from '@/scopes/search/search/domain/queryStringToSearchStateMapper';
import { searchStore } from '@/scopes/search/search/infrastructure/store/store';
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary';
import { Custom500 } from '@/pages/500';
import { roomsParticipantsDistributionStore } from '@/scopes/common/components/Filters/Participants/store';
import { FiltersModal } from './components/FiltersModal';
import { Footer } from '@/scopes/common/components';
import { SummerContent } from '@/scopes/common/components/Footer/SummerContent';
import { Hero } from './components/Hero';
import { type DateOrRange, departureDateStore } from '@/infrastructure/departure-date/store';
import { searchQueryClient } from '@/scopes/search/search/infrastructure/query-client/searchQueryClient';
import { Header } from '@/scopes/common/components/Header';
import { DevTools } from '@/scopes/common/components/DevTools/DevTools';
import { DepartureAirportQuickSearchItemFilter } from './components/QuickSearchFilters/DepartureAiportFilter';
import { DepartureDateQuickSearchItemFilter } from './components/QuickSearchFilters/DepartureDateFilter';
import { getFlexibleRangeForDepartureDate } from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DepartureDateFilter/utils';
import { DurationQuickSearchItemFilter } from './components/QuickSearchFilters/DurationFilter';
import { ParticipantsQuickSearchItemFilter } from './components/QuickSearchFilters/ParticipantsFilter';
import { Results } from './components/Results';
import { SideBar } from './components/SideBar';
import { OptimizelyFlagProvider } from '@/scopes/common/infrastructure/ab-testing/useOptimizely';
import { useDecision } from '@optimizely/react-sdk';
import { useCMSPageField } from '@/cms/fields';
import { createSearchApiClient } from '@/scopes/search/search/infrastructure/api/searchApiClient';
import type { SearchFilters } from '@/scopes/search/search/infrastructure/api/types';
import { SearchDependenciesProvider, useSearchDependencies } from './context';
import * as React from 'react';
import { QuickSearchContent } from '@/scopes/search/components/QuickSearchBar/QuickSearchContent';
import { QuickSearchBar } from '../../../components/QuickSearchBar';
import { useCatalogueConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider';
import { createCampaignDetailsApiClient } from '@/scopes/search/search/infrastructure/campaign-details/campaignDetailsApiClient';
import { getDefaultFilterValues } from '@/scopes/search/search/domain/policies/publicationCodes';
import type { PublicationCode } from '@/scopes/common/domain/types';
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams';
import { filterSelectionQueryParamHandler as createFilterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler';
import { searchPassThroughParams, type SearchPassThroughParams } from '@/scopes/search/types';
import { passThroughQueryParamHandler } from '@/scopes/search/search/domain/passThroughQueryParamHandler';

function Search() {
  const { filterSelectionQueryParamHandler, passThroughParamHandler } = useSearchDependencies();
  const { isLoading, isFetching } = useResults();
  const publicationCode = useCatalogueConfiguration('summer', 'publicationCode');

  React.useEffect(() => {
    const defaultValues = getDefaultFilterValues(publicationCode as PublicationCode); // TODO change this `as` when configurations are complete
    const passThroughParams = passThroughParamHandler.get();
    const queryStringToSearchStateMapper = createQueryStringToSearchStateMapper({ defaultValues });
    const initialState = queryStringToSearchStateMapper.map(window.location.search, passThroughParams);

    searchStore.setState(initialState);
    filterSelectionQueryParamHandler.update();

    if (initialState.filterSelection?.DepartureDate) {
      const departureDate = initialState.filterSelection?.DepartureDate as DateOrRange;

      departureDateStore.setState({
        departureDate,
        flexibleRange: getFlexibleRangeForDepartureDate(departureDate),
        originDate: '',
        view: 'calendar',
      });
    }
    if (initialState.filterSelection?.Participants) {
      roomsParticipantsDistributionStore.setState({
        roomsParticipantDistribution: initialState.filterSelection?.Participants,
      });
    }
  }, []);

  return (
    <NewRelicErrorBoundary scope="search-page" fallback={<Custom500 brand="sunweb" />}>
      <Wrapper>
        <Hero>
          {!isLoading && (
            <QuickSearchBar disabled={isFetching}>
              <QuickSearchContent disabled={isFetching}>
                <DepartureDateQuickSearchItemFilter />
                <DurationQuickSearchItemFilter />
                <ParticipantsQuickSearchItemFilter />
                <DepartureAirportQuickSearchItemFilter />
              </QuickSearchContent>
            </QuickSearchBar>
          )}
        </Hero>
        <Content>
          {isLoading ? (
            <StyledLoadingSpinner />
          ) : (
            <>
              <SideBar />
              <Results />
              <FiltersModal />
            </>
          )}
        </Content>
      </Wrapper>
    </NewRelicErrorBoundary>
  );
}

function Page() {
  const [yieldCampaignDecision] = useDecision('ID_263_yield_campaign_image');
  const [hideUspDecision] = useDecision('ID_298_hide_usp_mobile');
  const [productAdsDecision] = useDecision('id_xxx_product_ads_integration_plp');
  const [hideCarouselDecision] = useDecision('ID_349_hide_carousel');
  const contextItemId = useCMSPageField('contextItemId');

  const browserQuery = createBrowserQueryStringKeepingParams<SearchPassThroughParams>({
    passThroughParams: searchPassThroughParams,
  });
  const passThroughParamHandler = passThroughQueryParamHandler<SearchPassThroughParams>({
    passThroughParams: searchPassThroughParams,
    browserQuery,
  });

  return (
    <SearchDependenciesProvider
      dependencies={{
        filterSelectionQueryParamHandler: createFilterSelectionQueryParamHandler({
          store: searchStore,
          browserQuery,
        }),
        searchAPIClient: createSearchApiClient<SearchFilters>({
          contextItemId,
          passThroughParamHandler,
        }),
        campaignDetailsApiClient: createCampaignDetailsApiClient({ contextItemId }),
        passThroughParamHandler,
      }}
    >
      <OptimizelyFlagProvider
        value={{
          yieldCampaignImageFlag: yieldCampaignDecision.enabled,
          hideUspMobile: hideUspDecision.enabled,
          productAdsIntegration: productAdsDecision.enabled,
          hideCarousel: hideCarouselDecision.enabled,
        }}
      >
        <QueryClientProvider client={searchQueryClient}>
          <Header />
          <Search />
          <Footer>
            <SummerContent />
          </Footer>
          <DevTools />
        </QueryClientProvider>
      </OptimizelyFlagProvider>
    </SearchDependenciesProvider>
  );
}

const StyledLoadingSpinner = styled(LoadingSpinner)`
  margin-top: 80px;
`;

const Wrapper = styled.div`
  background: ${tk('color_semantic_bg_subdued')};
  min-height: 100vh;
`;

const Content = styled.div`
  display: flex;
  position: relative;
  justify-content: center;

  max-width: ${tk('breakpoint_desktop_xl')};
  margin: auto;
`;

export default Page;
